import classNames from 'classnames';
import React, {useEffect, useRef, useState} from 'react';
import {useTextField} from '@react-aria/textfield';
import {InputContainer, useInput} from '@time-webkit/all/atoms/input';
import {TypographySize, useTypography, type TypographyProp} from '@time-webkit/all/hooks';
import {CloseIcon} from '@time-webkit/all/icons/close';
import type {AriaTextFieldProps} from '@react-types/textfield';

import {useAutosizeTextarea} from './hooks/use-autosize-textarea';

import styles from './textarea.module.css';

export type TextareaProps = {

    /**
     * @default none
     */
    counterVariant?: 'none' | 'visible' | 'limit';
    characterLimit?: number;
    className?: string;
    description?: string;
    errorMessage?: string;
    onFocus?: React.FocusEventHandler;
    onBlur?: React.FocusEventHandler;

    /**
     * @default 6
     */
    maxRows?: number;

    /**
     * @default 1
     */
    minRows?: number;
} & Omit<AriaTextFieldProps, 'onBlur' | 'onFocus' | 'errorMessage' | 'description'>

const typographyProps: TypographyProp[] = [
    {size: TypographySize.BodyM, noDefaultColor: true},
    {size: TypographySize.BodyMTrue, noDefaultColor: true},
    {size: TypographySize.BodyS, noDefaultColor: true},
];

export const Textarea = ({
    counterVariant = 'none',
    characterLimit,
    className,
    description,
    errorMessage,
    maxRows = 6,
    minRows = 1,
    onChange,
    ...textFieldProps
}: TextareaProps) => {
    const [value, setValue] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [bodyMCls, bodyMTrueCls, bodySCls] = useTypography(typographyProps);

    const handleChange = (newValue: string) => {
        setValue(newValue);
        onChange?.(newValue);
    };

    useEffect(() => {
        setValue(textFieldProps.value ?? '');
    }, [textFieldProps.value]);

    const {inputProps, labelProps, descriptionProps, errorMessageProps} = useTextField<'textarea'>({
        ...textFieldProps,
        onChange: handleChange,
        validationState: errorMessage ? 'invalid' : 'valid',
        value,
    }, textareaRef);

    const isCharsLimitExceeded = Boolean(characterLimit && value.length > characterLimit);

    const containerProps = useInput({
        inputRef: textareaRef,
        inputId: inputProps.id!,
        value,
        isDisabled: textFieldProps.isDisabled,
    });

    useAutosizeTextarea({
        textareaRef,
        value,
        minRows,

        // Предотвращаем прыжок при появлении счётчика
        maxRows: counterVariant === 'limit' && isCharsLimitExceeded ? maxRows - 1 : maxRows,
    });

    const handleClear = () => {
        handleChange('');
    };

    const renderCounter = () => {
        if (!characterLimit || counterVariant === 'none') {
            return null;
        }

        let text = null;

        if (counterVariant === 'visible') {
            text = `${value.length}/${characterLimit}`;
        }

        if (counterVariant === 'limit' && isCharsLimitExceeded) {
            text = `–${value.length - characterLimit}`;
        }

        return text && (
            <div
                className={classNames(styles.count, bodySCls, {[styles.error]: isCharsLimitExceeded})}
                data-testid='textarea-counter'
            >
                {text}
            </div>
        );
    };

    return (
        <div className={className}>
            {textFieldProps.label && (
                <label {...labelProps} className={classNames(styles.label, bodyMTrueCls)}>{textFieldProps.label}</label>
            )}

            <InputContainer
                {...containerProps}
                className={classNames(styles.inputContainer)}
                error={Boolean(errorMessage)}
                size='medium'
                sizeClassName={styles.medium}
                wrapperClassName={styles.contentWrapper}
            >
                <textarea
                    {...inputProps}
                    aria-errormessage={errorMessageProps.id}
                    className={classNames(styles.textarea, bodyMCls)}
                    ref={textareaRef}
                    rows={1}
                />

                {value && !inputProps.disabled && (
                    <button className={styles.closeButton} onClick={handleClear} tabIndex={-1}>
                        <CloseIcon width={16} height={16} />
                    </button>
                )}

                {renderCounter()}
            </InputContainer>

            {description && !errorMessage && (
                <p {...descriptionProps} className={classNames(styles.description, bodyMCls)}>
                    {description}
                </p>
            )}

            {errorMessage && (
                <p {...errorMessageProps} className={classNames(styles.description, styles.error, bodyMCls)}>
                    {errorMessage}
                </p>
            )}
        </div>
    );
};
